import { HttpClient, HttpContext, HttpContextToken, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import type { DataAvailabilityDetailsResponse } from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import { ReportingApiHttpQuery } from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import { ReportingApiBaseUrlResolverService } from './reporting-api-base-url-resolver.service';

export { REPORTING_API_CONFIG } from './reporting-api.config';
export const REPORTING_API_REQUEST = new HttpContextToken<boolean>(() => false);

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: unknown;
  body?: unknown;
  headers?: Record<string, string>;
  responseType?: 'json' | 'text' | 'blob';
}

export interface ReportingApiBlobResponse {
  blob: Blob;
  fileName: string | null;
  mimeType: string | null;
}

@Injectable({ providedIn: 'root' })
export class ReportingApiService {
  constructor(
    private readonly http: HttpClient,
    private readonly reportingApiBaseUrlResolverService: ReportingApiBaseUrlResolverService,
  ) {}

  getHealth = (): Promise<{ ok: boolean }> => this.request<{ ok: boolean }>('/api/health');

  getReportDataAvailability = (): Promise<DataAvailabilityDetailsResponse> =>
    this.request<DataAvailabilityDetailsResponse>('/api/reporting/data-availability');

  getReportPdf = (query: ReportingApiHttpQuery): Promise<Blob> =>
    this.request<Blob>('/api/reports/pdf', {
      method: 'GET',
      query,
      responseType: 'blob',
    });

  request = <T>(path: string, options: RequestOptions = {}): Promise<T> => {
    return this.send<T>(path, options);
  };

  requestBlob = (
    path: string,
    options: Omit<RequestOptions, 'responseType'> = {},
  ): Promise<ReportingApiBlobResponse> => {
    return this.sendBlob(path, options);
  };

  async getUrl(path: string, query?: unknown): Promise<string> {
    return this.reportingApiBaseUrlResolverService.resolveUrl(path, query);
  }

  private async send<T>(path: string, options: RequestOptions): Promise<T> {
    const method = options.method ?? 'GET';
    const responseType: 'json' | 'text' | 'blob' = options.responseType ?? 'json';
    const hasBody = options.body !== undefined && options.body !== null;
    const isFormDataBody = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const headers = new HttpHeaders({
      ...(hasBody && !isFormDataBody ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers ?? {}),
    });
    const response =
      responseType === 'blob'
        ? await firstValueFrom(
            this.http.request(method, path, {
              body: hasBody ? (isFormDataBody ? (options.body as FormData) : options.body) : undefined,
              context: new HttpContext().set(REPORTING_API_REQUEST, true),
              headers,
              observe: 'response',
              params: this.buildHttpParams(options.query),
              responseType: 'blob',
            }),
          )
        : responseType === 'text'
          ? await firstValueFrom(
              this.http.request(method, path, {
                body: hasBody ? (isFormDataBody ? (options.body as FormData) : options.body) : undefined,
                context: new HttpContext().set(REPORTING_API_REQUEST, true),
                headers,
                observe: 'response',
                params: this.buildHttpParams(options.query),
                responseType: 'text',
              }),
            )
          : await firstValueFrom(
              this.http.request(method, path, {
                body: hasBody ? (isFormDataBody ? (options.body as FormData) : options.body) : undefined,
                context: new HttpContext().set(REPORTING_API_REQUEST, true),
                headers,
                observe: 'response',
                params: this.buildHttpParams(options.query),
                responseType: 'json',
              }),
            );

    return (response.body ?? undefined) as T;
  }

  private async sendBlob(
    path: string,
    options: Omit<RequestOptions, 'responseType'>,
  ): Promise<ReportingApiBlobResponse> {
    const method = options.method ?? 'GET';
    const hasBody = options.body !== undefined && options.body !== null;
    const isFormDataBody = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const headers = new HttpHeaders({
      ...(hasBody && !isFormDataBody ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers ?? {}),
    });
    const response = await firstValueFrom(
      this.http.request(method, path, {
        body: hasBody ? (isFormDataBody ? (options.body as FormData) : options.body) : undefined,
        context: new HttpContext().set(REPORTING_API_REQUEST, true),
        headers,
        observe: 'response',
        params: this.buildHttpParams(options.query),
        responseType: 'blob',
      }),
    );
    const blob = response.body ?? new Blob();

    return {
      blob,
      fileName: this.getFileNameFromContentDisposition(response.headers.get('content-disposition')),
      mimeType: blob.type || response.headers.get('content-type'),
    };
  }

  private getFileNameFromContentDisposition(contentDisposition: string | null): string | null {
    if (!contentDisposition) {
      return null;
    }

    const utf8FileNameMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utf8FileNameMatch?.[1]) {
      return decodeURIComponent(utf8FileNameMatch[1].trim());
    }

    const fileNameMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
    return fileNameMatch?.[1]?.trim() || null;
  }

  private buildHttpParams(query?: unknown): HttpParams {
    if (!query || typeof query !== 'object') {
      return new HttpParams();
    }

    let params = new HttpParams();
    Object.entries(query as Record<string, unknown>).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }

      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        params = params.set(key, String(value));
        return;
      }

      if (Array.isArray(value)) {
        value.forEach(item => {
          if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
            params = params.append(key, String(item));
          }
        });
        return;
      }

      params = params.set(key, JSON.stringify(value));
    });

    return params;
  }
}
